import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import SMCDayPickerInput from '../../../components/Simple/SMCDayPickerInput';
import FilterLayout from '../FilterLayout';
import RowLayout from '../../../components/Layouts/RowLayout';
import SMCExpansionPanel from '../../../components/Simple/SMCExpansionPanel';
import SMCIcon from '../../../components/Simple/SMCIcon';
import {SMCSelect, SMCOption} from '../../../components/Simple/SMCSelect';
import {FirmwareFilterSchema} from '../../../api/DBSchema/Firmware';

function DateRangeChooser({
    fromDate,
    toDate,
    setFromDate,
    setToDate
}) {
    return (
        <>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>From:</span>
            <SMCDayPickerInput
                placeholder="From..."
                value={fromDate}
                onChange={setFromDate}
                maxDate={toDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setFromDate(null)} />
        </RowLayout>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>To:</span>
            <SMCDayPickerInput
                placeholder="To..."
                value={toDate}
                onChange={setToDate}
                minDate={fromDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setToDate(null)} />
        </RowLayout>
        </>
    );
}

function FirmwareFilterPane({
    initialFilter,
    onFilterChange,
    open
}) {
    const { t } = useTranslation();
    const didMountRef = useRef(true);
    const freeTextSearchRef = useRef();

    useEffect(() => {
        if (open && freeTextSearchRef.current) {
            freeTextSearchRef.current.focus();
        }
    }, [open]);

    const [freeTextSearch, setFreeTextSearch] = useState(
        initialFilter.freeTextSearch ? initialFilter.freeTextSearch.value : ''
    );

    const [appliance, setAppliance] = useState(
        initialFilter.appliance ? initialFilter.appliance.value : ''
    );

    const [type, setType] = useState(
        initialFilter.type ? initialFilter.type.value : ''
    );
        
    const [creationFromDate, setCreationFromDate] = useState(null);
    const [creationToDate, setCreationToDate] = useState(null);

    const [modificationFromDate, setModificationFromDate] = useState(null);
    const [modificationToDate, setModificationToDate] = useState(null);
    

    const resetToDefaults = useCallback(() => {
        setFreeTextSearch('');
        setAppliance('');
        setType('');

        setCreationFromDate(null);
        setCreationToDate(null);
        
        setModificationFromDate(null);
        setModificationToDate(null);
    }, []);


    useEffect(() => {
        // Don't run on first render
        if (didMountRef.current === true) {
            didMountRef.current = false;
            return;
        }

        const filterRequestObj = {};
        // Free text search
        if (freeTextSearch) {
            filterRequestObj[FirmwareFilterSchema.freeTextSearch] = { value: freeTextSearch };
        }
        // Appliance
        if (appliance) {
            filterRequestObj[FirmwareFilterSchema.appliance] = { value: appliance };
        }
        // Type
        if (type) {
            filterRequestObj[FirmwareFilterSchema.type] = { value: type };
        }
        // Creation date range
        if (creationFromDate || creationToDate) {
            filterRequestObj[FirmwareFilterSchema.creationDate] = {
                value: {
                    from: creationFromDate ? creationFromDate.getTime() : null,
                    to: creationToDate ? creationToDate.getTime() : null
                }
            };
        }
        // Modification date range
        if (modificationFromDate || modificationToDate) {
            filterRequestObj[FirmwareFilterSchema.modificationDate] = {
                value: {
                    from: modificationFromDate ? modificationFromDate.getTime() : null,
                    to: modificationToDate ? modificationToDate.getTime() : null
                }
            };
        }
        
        onFilterChange(filterRequestObj);
    }, [
        freeTextSearch, onFilterChange,
        appliance, type,
        creationFromDate, creationToDate,
        modificationFromDate, modificationToDate,
    ]);

    return (
        <FilterLayout clearAllAction={resetToDefaults}>
            {/* Status tab */}
            <SMCExpansionPanel title={t('SMP.FIRMWARE.APPLIANCE')}>
                <SMCSelect width="190px" value={appliance} onChange={e => setAppliance(e.target.value)}>
                    <SMCOption value="">{t('SMC_GEN_ANY')}</SMCOption>
                    <SMCOption value={FirmwareFilterSchema.applianceValues.type600}>{FirmwareFilterSchema.applianceValues.type600}</SMCOption>
                    <SMCOption value={FirmwareFilterSchema.applianceValues.type1200}>{FirmwareFilterSchema.applianceValues.type1200}</SMCOption>
                    <SMCOption value={FirmwareFilterSchema.applianceValues.typeS1S2}>{FirmwareFilterSchema.applianceValues.typeS1S2}</SMCOption>
                    <SMCOption value={FirmwareFilterSchema.applianceValues.typeV0V1}>{FirmwareFilterSchema.applianceValues.typeV0V1}</SMCOption>
                    <SMCOption value={FirmwareFilterSchema.applianceValues.typeV2V3}>{FirmwareFilterSchema.applianceValues.typeV2V3}</SMCOption>
                </SMCSelect>
            </SMCExpansionPanel>
            {/* Type tab */}
            <SMCExpansionPanel title={t('SMP.FIRMWARE.TYPE')}>
                <SMCSelect width="190px" value={type} onChange={e => setType(e.target.value)}>
                    <SMCOption value="">{t('SMC_GEN_ANY')}</SMCOption>
                    <SMCOption value={FirmwareFilterSchema.typeValues.generalAvailability}>{FirmwareFilterSchema.typeValues.generalAvailability}</SMCOption>
                    <SMCOption value={FirmwareFilterSchema.typeValues.customerHotfix}>{FirmwareFilterSchema.typeValues.customerHotfix}</SMCOption>
                </SMCSelect>
            </SMCExpansionPanel>
            {/* Creation tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_CREATETIME')}>
                <DateRangeChooser
                    fromDate={creationFromDate}
                    toDate={creationToDate}
                    setFromDate={setCreationFromDate}
                    setToDate={setCreationToDate}
                />
            </SMCExpansionPanel>
            {/* Modification tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_MODIFYTIME')}>
                <DateRangeChooser
                    fromDate={modificationFromDate}
                    toDate={modificationToDate}
                    setFromDate={setModificationFromDate}
                    setToDate={setModificationToDate}
                />
            </SMCExpansionPanel>
        </FilterLayout>
    );
}

export default React.memo(FirmwareFilterPane);